import React, { useState, useEffect } from "react";
import { Text, Row, Column } from "@amzn/meridian";
import Checkbox from "@amzn/meridian/checkbox";
import { formatAttestationDateTime } from "src/utils/dateUtil";
import "./CheckpointVerifyFooter.scss";

interface CheckpointVerifyFooterProps {
    verified: boolean;
    verifiedBy?: string | null;
    verifiedAt?: string | null;
    /** Whether the checkbox is interactive (checkpoint compliant or fixed-later). */
    enabled: boolean;
    /** Should reject on failure so the optimistic tick can roll back. */
    onVerify: (verified: boolean) => void | Promise<void>;
}

/**
 * Verify footer on a checkpoint card. Amber prompt when unverified, green when
 * verified. The checkbox flips optimistically to hide the verify + re-fetch latency.
 */
const CheckpointVerifyFooter: React.FC<CheckpointVerifyFooterProps> = ({
    verified,
    verifiedAt,
    enabled,
    onVerify,
}) => {
    // Optimistic mirror of `verified`, re-synced when the server value changes.
    const [optimisticVerified, setOptimisticVerified] = useState(verified);

    useEffect(() => {
        setOptimisticVerified(verified);
    }, [verified]);

    const handleChange = async (checked: boolean) => {
        setOptimisticVerified(checked);
        try {
            await onVerify(checked);
        } catch {
            setOptimisticVerified(verified); // roll back on failure
        }
    };

    if (optimisticVerified) {
        return (
            <div className="checkpoint-verify-footer checkpoint-verify-footer--verified">
                <Row alignmentVertical="center" spacing="300" spacingInset="400">
                    <Checkbox size="small" checked={true} onChange={handleChange}>
                        Verified
                        {verifiedAt ? ` on ${formatAttestationDateTime(verifiedAt)}` : ""}
                    </Checkbox>
                </Row>
            </div>
        );
    }

    // Disabled (non-compliant) checkpoints get a neutral gray instead of amber.
    const stateModifier = enabled
        ? "checkpoint-verify-footer--prompt"
        : "checkpoint-verify-footer--disabled";

    return (
        <div className={`checkpoint-verify-footer ${stateModifier}`}>
            <Column spacing="300" spacingInset="400">
                <Text type="b100" color="secondary">
                    Review compliance status above and verify the AI result before you confirm
                    this checkpoint.
                </Text>
                <Checkbox size="small" checked={false} disabled={!enabled} onChange={handleChange}>
                    Verify Compliance
                </Checkbox>
            </Column>
        </div>
    );
};

export default CheckpointVerifyFooter;
